import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { PermissionService } from '../../../shared/services/permission.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { WhatsappAccountApiService } from '../services/whatsapp-account-api.service';
import { WhatsappMetaSdkService } from '../services/whatsapp-meta-sdk.service';
import {
  ManualConnectRequest,
  WhatsappAccountResponse,
  WhatsappPhoneResponse,
  WhatsappSettingsResponse,
  WHATSAPP_ACCOUNT_STATUS_LABELS,
  WHATSAPP_QUALITY_RATING_LABELS,
  whatsappLabel,
} from '../models/whatsapp.model';
import { WhatsappAssignmentModalComponent } from '../whatsapp-assignment-modal/whatsapp-assignment-modal.component';

@Component({
  selector: 'app-whatsapp-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    PageBreadcrumbComponent,
    WhatsappAssignmentModalComponent,
  ],
  templateUrl: './whatsapp-setup.component.html',
})
export class WhatsappSetupComponent implements OnInit {
  private readonly accountApi = inject(WhatsappAccountApiService);
  private readonly metaSdk = inject(WhatsappMetaSdkService);
  private readonly authService = inject(AuthService);
  private readonly confirm = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly permissions = inject(PermissionService);
  private readonly router = inject(Router);

  loading = true;
  saving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  account: WhatsappAccountResponse | null = null;

  /**
   * Ajustes del canal. Se piden aparte de la cuenta porque el endpoint responde 200 aunque
   * no haya cuenta conectada (`connected: false`): así el interruptor se pinta igual, sólo
   * que deshabilitado, en vez de desaparecer sin explicación.
   */
  settings: WhatsappSettingsResponse | null = null;
  settingsLoading = false;
  settingsSaving = false;
  /** Valor que muestra el interruptor. Se revierte solo si el PUT falla. */
  autoCreateContacts = false;

  assignmentModalOpen = false;
  assignmentTarget: WhatsappPhoneResponse | null = null;

  /** Conexión manual: alternativa al Embedded Signup mientras Meta no aprueba la app. */
  manualOpen = false;
  manualForm: ManualConnectRequest = {
    wabaId: '',
    accessToken: '',
    phoneNumberId: '',
    displayPhoneNumber: '',
  };

  ngOnInit(): void {
    if (!this.isAdmin()) {
      this.router.navigate(['/whatsapp/chat']);
      return;
    }
    this.loadAccount();
    this.loadSettings();
  }

  isAdmin(): boolean {
    const name = this.authService.getCurrentUser()?.roleName ?? '';
    return name === 'Admin' || name === 'Super Admin';
  }

  loadAccount(): void {
    this.loading = true;
    this.errorMessage = null;
    this.accountApi.getAccount().subscribe({
      next: (acc) => {
        this.account = acc;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.account = null;
        } else {
          this.errorMessage = this.toErrorMessage(err, 'No se pudo cargar la cuenta');
        }
        this.loading = false;
      },
    });
  }

  // ===== Alta automática de contactos =====

  loadSettings(): void {
    this.settingsLoading = true;
    this.accountApi.getSettings().subscribe({
      next: (s) => {
        this.settings = s;
        this.autoCreateContacts = s.autoCreateContacts;
        this.settingsLoading = false;
      },
      error: () => {
        // Silencioso: el ajuste es secundario a la conexión, y el banner de arriba ya
        // reporta lo que de verdad bloquea la pantalla. El interruptor queda deshabilitado.
        this.settings = null;
        this.settingsLoading = false;
      },
    });
  }

  /**
   * ¿El rol puede guardar el ajuste? Misma matriz que usa `appDisableWithoutPermission`,
   * pero consultada desde aquí: el interruptor tiene además el bloqueo por «cuenta no
   * conectada», y dos dueños del atributo `disabled` (la directiva por un lado y el
   * `[disabled]` de Angular por el otro) se pisan — el último en correr gana y el control
   * podía quedar habilitado sin permiso. Un único dueño evita esa carrera.
   */
  get puedeEditarPermiso(): boolean {
    return this.permissions.has('whatsapp', 'edit');
  }

  /** El interruptor sólo se puede tocar con la cuenta conectada (el PUT devolvería 409). */
  get canEditSettings(): boolean {
    return (
      this.puedeEditarPermiso &&
      !!this.settings?.connected &&
      !this.settingsLoading &&
      !this.settingsSaving
    );
  }

  /**
   * Guarda el ajuste en cuanto se mueve el interruptor: es un único campo, y obligar a
   * pulsar «Guardar» para un booleano sólo añade un paso que se olvida.
   */
  onAutoCreateContactsChange(valor: boolean): void {
    const anterior = this.settings?.autoCreateContacts ?? false;
    if (valor === anterior) return;

    this.settingsSaving = true;
    this.errorMessage = null;
    this.accountApi.updateSettings({ autoCreateContacts: valor }).subscribe({
      next: (s) => {
        this.settings = s;
        this.autoCreateContacts = s.autoCreateContacts;
        this.settingsSaving = false;
        this.toast.success({
          title: 'Ajuste guardado',
          message: s.autoCreateContacts
            ? 'Cada número nuevo que escriba creará su ficha de contacto automáticamente.'
            : 'Los mensajes seguirán llegando a la bandeja, pero ya no se creará una ficha por cada número nuevo.',
        });
      },
      error: (err: HttpErrorResponse) => {
        this.settingsSaving = false;
        // Se revierte el interruptor: dejarlo movido haría creer que el cambio se guardó.
        this.autoCreateContacts = anterior;
        const msg =
          err.status === 409
            ? 'Este negocio todavía no tiene WhatsApp conectado. Conectá la cuenta antes de cambiar sus ajustes.'
            : err.status === 403
              ? 'No tenés permiso para cambiar los ajustes de WhatsApp. Sólo un Admin o Super Admin puede hacerlo.'
              : this.toErrorMessage(err, 'No se pudo guardar el ajuste');
        this.errorMessage = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  connect(): void {
    if (!this.metaSdk.isConfigured()) {
      this.confirm.alert({
        title: 'Configuración incompleta',
        message:
          'Faltan variables de Meta (whatsapp.appId y whatsapp.configId). Pídele al administrador del sistema que las configure en environment.',
        variant: 'warning',
      });
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    this.successMessage = null;

    this.metaSdk
      .runEmbeddedSignup()
      .then((code) => {
        this.accountApi.exchange({ code }).subscribe({
          next: (acc) => {
            this.account = acc;
            this.saving = false;
            this.successMessage = 'WhatsApp conectado correctamente';
            // Con la cuenta recien conectada el ajuste pasa a ser editable.
            this.loadSettings();
          },
          error: (err) => {
            this.saving = false;
            this.errorMessage = this.toErrorMessage(err, 'No se pudo completar el onboarding');
          },
        });
      })
      .catch((err) => {
        this.saving = false;
        this.errorMessage = err?.message || 'Embedded Signup cancelado';
      });
  }

  connectManually(): void {
    const wabaId = (this.manualForm.wabaId || '').trim();
    const accessToken = (this.manualForm.accessToken || '').trim();
    if (!wabaId || !accessToken) {
      this.errorMessage = 'El WABA ID y el access token son obligatorios.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    this.successMessage = null;

    const phoneNumberId = (this.manualForm.phoneNumberId || '').trim();
    const displayPhoneNumber = (this.manualForm.displayPhoneNumber || '').trim();

    this.accountApi
      .connectManually({
        wabaId,
        accessToken,
        ...(phoneNumberId ? { phoneNumberId } : {}),
        ...(displayPhoneNumber ? { displayPhoneNumber } : {}),
      })
      .subscribe({
        next: (acc) => {
          this.account = acc;
          this.saving = false;
          this.manualOpen = false;
          this.manualForm = { wabaId: '', accessToken: '', phoneNumberId: '', displayPhoneNumber: '' };
          this.successMessage = 'WhatsApp conectado manualmente';
          this.loadSettings();
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage = this.toErrorMessage(err, 'No se pudo conectar manualmente');
        },
      });
  }

  async disconnect(): Promise<void> {
    const ok = await this.confirm.confirmDanger({
      title: 'Desconectar WhatsApp',
      message:
        'Se desactivará el envío. El histórico de conversaciones y asignaciones se conserva. ¿Continuar?',
      confirmText: 'Desconectar',
    });
    if (!ok) return;
    this.saving = true;
    this.accountApi.disconnect().subscribe({
      next: () => {
        this.account = null;
        this.saving = false;
        this.successMessage = 'WhatsApp desconectado';
        this.loadSettings();
      },
      error: (err) => {
        this.saving = false;
        this.errorMessage = this.toErrorMessage(err, 'No se pudo desconectar');
      },
    });
  }

  openAssign(phone: WhatsappPhoneResponse): void {
    this.assignmentTarget = phone;
    this.assignmentModalOpen = true;
  }

  closeAssign(): void {
    this.assignmentModalOpen = false;
    this.assignmentTarget = null;
  }

  reload(): void {
    this.loadAccount();
    this.loadSettings();
  }

  accountStatusLabel(status: string | null | undefined): string {
    return whatsappLabel(WHATSAPP_ACCOUNT_STATUS_LABELS, status);
  }

  qualityRatingLabel(rating: string | null | undefined): string {
    return whatsappLabel(WHATSAPP_QUALITY_RATING_LABELS, rating, 'Sin calificar');
  }

  qualityClass(rating?: string | null): string {
    switch ((rating || '').toUpperCase()) {
      case 'GREEN':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
      case 'YELLOW':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
      case 'RED':
        return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  }

  statusBanner(): { variant: 'warning' | 'danger' | null; text: string } {
    const s = (this.account?.status || '').toUpperCase();
    if (s === 'TOKEN_EXPIRING') {
      return {
        variant: 'warning',
        text: 'El token vence pronto. Reconectá WhatsApp antes del vencimiento para evitar interrupciones.',
      };
    }
    if (s === 'TOKEN_EXPIRED') {
      return {
        variant: 'danger',
        text: 'El token venció. Reconectá WhatsApp para reanudar el envío de mensajes.',
      };
    }
    if (s === 'ERROR') {
      return {
        variant: 'danger',
        text: this.account?.lastError || 'Hay un error con la cuenta de WhatsApp.',
      };
    }
    return { variant: null, text: '' };
  }

  private toErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body: any = err.error;
      if (typeof body === 'string') return body;
      if (body?.message) return body.message;
      if (body?.error) return body.error;
      return `${err.status} ${err.statusText || fallback}`;
    }
    return fallback;
  }
}
